// src/pages/MallManagementUserAuth.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Paper,
  Grid,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Divider,
  useMediaQuery,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import Services from 'src/services/Services';
import MallManagementAddUserModal from 'src/components/modals/MallManagementAddUserModal';

const MallManagementUserAuth = () => {
  const theme = useTheme();

  const mallID = localStorage.getItem('mallID');
  const parsedUser = JSON.parse(localStorage.getItem('user'));

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({});

  // Modal states
  const [dialogOpen, setDialogOpen] = useState(false);

  // Check if current user has mall-management permission
  const hasMallManagementAuth = parsedUser?.frontAuth?.includes('mall-management');

  useEffect(() => {
    if (!mallID || !parsedUser?.token) {
      toast.error("Oturum bilgileri bulunamadı. Lütfen tekrar giriş yapın.");
      return;
    }

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await Services.GetUsersByType(parsedUser.token, mallID, '1', 'user');
        if (response.data.status) {
          const fetchedUsers = response.data.result || [];
          const filteredUsers = fetchedUsers.filter(
            (user) => !(user.frontAuth || []).includes('mall-management')
          );
          setUsers(filteredUsers);
          if (filteredUsers.length > 0) {
            setSelectedUserId(filteredUsers[0].id);
          } else {
            setSelectedUserId('');
          }

        } else {
          toast.error('Kullanıcılar getirilirken bir hata oluştu.');
        }
      } catch (error) {
        toast.error('Kullanıcıları getirme servisine ulaşılamadı.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [mallID, parsedUser?.token]);



  useEffect(() => {
    if (!selectedUserId || users.length === 0) return;

    const currentUser = users.find(user => user.id === selectedUserId);

    if (currentUser) {
      const allPermissionsTemplate = {
        'mall-management-goods-acceptance': false,
        'mall-management-correspondence': false,
        'mall-management-safety-locker': false,
        'mall-management-safety-cloakroom': false,
        'mall-management-clickAndCollect': false,
        'mall-management-cargo': false,
        'mall-management-home-delivery': false,
        'mall-management-tax-free': false,
        'mall-management-pet-car': false,
        'mall-management-kid-car': false,
        'mall-management-handsfree': false,
        'mall-management-postbox': false,
        'mall-management-gift': false,
        'admin': false,
        'mall-management-statistics': false,
        'mall-management-technical-support' : false
      };

      const userAuths = currentUser.frontAuth || [];

      userAuths.forEach(auth => {
        if (allPermissionsTemplate.hasOwnProperty(auth)) {
          allPermissionsTemplate[auth] = true;
        }
      });
      setPermissions(allPermissionsTemplate);
    }
  }, [selectedUserId, users]);

  const handlePermissionChange = (event) => {
    const { name, checked } = event.target;
    if (name === 'admin') {
      const newPermissions = { ...permissions };
      for (const key in newPermissions) {
        newPermissions[key] = checked;
      }
      setPermissions(newPermissions);
    } else {
      setPermissions((prevPermissions) => ({
        ...prevPermissions,
        [name]: checked,
      }));
    }
  };

  const handleOpenAddUserModal = () => {
    setDialogOpen(true);
  };

  const getUserList = async (status = '1') => {
    try {
      const response = await Services.GetUsersByType(parsedUser.token, mallID, status, 'user');
      if (response.data.status) {
        const fetchedUsers = response.data.result || [];
        const filteredUsers = fetchedUsers.filter(
          (user) => !(user.frontAuth || []).includes('mall-management')
        );
        setUsers(filteredUsers);
        if (filteredUsers.length > 0) {
          setSelectedUserId(filteredUsers[0].id);
        } else {
          setSelectedUserId('');
        }
      }
    } catch (error) {
      console.error('Kullanıcılar yenilenirken hata:', error);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedUserId) {
      toast.warn('Lütfen bir kullanıcı seçin.');
      return;
    }
    setLoading(true);
    try {
      const currentUser = users.find(user => user.id === selectedUserId);
      if (!currentUser) {
        toast.error("Güncellenecek kullanıcı bulunamadı.");
        setLoading(false);
        return;
      }
      const originalFrontAuth = currentUser.frontAuth || [];
      const managedPermissionKeys = Object.keys(permissions);
      const legacyPermissions = originalFrontAuth.filter(
        (auth) => !managedPermissionKeys.includes(auth)
      );
      const newActiveUIPermissions = managedPermissionKeys.filter(key => permissions[key]);
      const finalFrontAuthArray = [...newActiveUIPermissions, ...legacyPermissions];
      const response = await Services.UserUpdate(parsedUser.token, finalFrontAuthArray, selectedUserId);

      if (response.data.status) {
        toast.success('Kullanıcı yetkileri başarıyla güncellendi!');
        const updatedUsersResponse = await Services.GetUsersByType(parsedUser.token, mallID, '1', 'user');
        if (updatedUsersResponse.data.status) {
          const fetchedUsers = updatedUsersResponse.data.result || [];
          const filteredUsers = fetchedUsers.filter(
            (user) => !(user.frontAuth || []).includes('mall-management')
          );
          setUsers(filteredUsers);
        }
      } else {
        toast.error(response.data.message || 'Yetkiler güncellenirken bir hata oluştu.');
      }
    } catch (error) {
      toast.error('Güncelleme servisine ulaşılamadı.');
    } finally {
      setLoading(false);
    }
  };

  const renderToggle = (label, desc, name) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
      <Box>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{desc}</Typography>
      </Box>
      <Switch
        size="small"
        name={name}
        checked={permissions[name] || false}
        onChange={handlePermissionChange}
        disabled={permissions.admin && name !== 'admin'}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: '#22C55E' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#22C55E' },
        }}
      />
    </Box>
  );

  const groupCardProps = {
    elevation: 0,
    sx: { bgcolor: '#F8FAFC', border: `1px solid #ECECEC`, borderRadius: 2, overflow: 'hidden', mb: 2 },
  };

  return (
    <>
      <Helmet><title>Kullanıcı İşlemleri</title></Helmet>
      <Box sx={{ px: 4, py: 3 }}>
        <Typography variant="h5">Kullanıcı İşlemleri</Typography>
        <Paper sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }} elevation={0}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Kullanıcı Yetkilendirme</Typography>
              <Typography variant="body2" color="text.secondary">Yetki Ayarları</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {hasMallManagementAuth && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddUserModal}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: 'rgba(0, 44, 101, 0.04)'
                    }
                  }}
                >
                  Yeni Kullanıcı
                </Button>
              )}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Kullanıcı</InputLabel>
                <Select value={selectedUserId} label="Kullanıcı" onChange={(e) => setSelectedUserId(e.target.value)} disabled={loading}>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>{`${user.name} ${user.lastname}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleSavePermissions} disabled={loading}>
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </Box>
          </Box>
          <Grid container spacing={3} sx={{ px: 3, py: 4 }}>
            <Grid item xs={12} md={4}>
              <Card {...groupCardProps}>
                <Box sx={{ px: 2, pt: 2 }}><Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontSize: 12, fontWeight: 500 }}>Panel Yetkileri</Typography></Box>
                {renderToggle('İstatistikler', 'Sistem istatistiklerini görüntüleme', 'mall-management-statistics')}
              </Card>
              <Card {...groupCardProps}>
                <Box sx={{ px: 2, pt: 2 }}><Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontSize: 12, fontWeight: 500 }}>Mall Logistics</Typography></Box>
                {renderToggle('Mal Kabul', 'Mal Kabul işlemleri', 'mall-management-goods-acceptance')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Muhaberat', 'Muhaberat yönetimi', 'mall-management-correspondence')}
              </Card>
              <Card {...groupCardProps}>
                <Box sx={{ px: 2, pt: 2 }}><Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontSize: 12, fontWeight: 500 }}>Easy Emanet</Typography></Box>
                {renderToggle('Emanet Dolap', 'Emanet dolabı yönetimi', 'mall-management-safety-locker')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Emanet Vestiyer', 'Vestiyer yönetimi', 'mall-management-safety-cloakroom')}
              </Card>
              <Card {...groupCardProps}>
                <Box sx={{ px: 2, pt: 2 }}><Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontSize: 12, fontWeight: 500 }}>Teknik İşlemler</Typography></Box>
                {renderToggle('Teknik Destek Talepleri', 'Destek Talep Yönetimi', 'mall-management-technical-support')}
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card {...groupCardProps}>
                <Box sx={{ px: 2, pt: 2 }}><Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontSize: 12, fontWeight: 500 }}>Easy Point</Typography></Box>
                {renderToggle('Click & Collect', 'Click & Collect hizmeti', 'mall-management-clickAndCollect')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Kargo', 'Kargo hizmeti', 'mall-management-cargo')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Eve Teslim', 'Eve teslim hizmeti', 'mall-management-home-delivery')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Tax Free', 'Tax Free hizmeti', 'mall-management-tax-free')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Pet Araba', 'Pet arabası hizmeti', 'mall-management-pet-car')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Çocuk Araba', 'Çocuk arabası hizmeti', 'mall-management-kid-car')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Hands Free', 'Hands Free hizmeti', 'mall-management-handsfree')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {renderToggle('Post Box', 'Post Box hizmeti', 'mall-management-postbox')}
                <Divider sx={{ mx: 2, color: '#EAEAEA' }} />
                {/* Easy Mall kaldırıldı, navConfig'de karşılığı yok */}
                {renderToggle('Hediye Paketi', 'Hediye paketi hizmeti', 'mall-management-gift')}
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ bgcolor: '#FFF8DB', border: `1px solid #FFE1BF`, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, pt: 2 }}><Typography variant="subtitle2" sx={{ color: '#92400E', fontSize: 13, fontWeight: 500 }}>Admin Yetkileri</Typography></Box>
                {renderToggle('Admin Yetkisi', 'Sistem yöneticisi', 'admin')}
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Add User Modal */}
      <MallManagementAddUserModal
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        getUserList={getUserList}
      />
    </>
  );
};

export default MallManagementUserAuth;